import { isEmpty, isNil } from 'ramda';
import { CardType } from '~client/model/card-type';
import { creditCard, CreditCardExpirationDate } from '~common/helper/credit-card';
import { datetime, localeBasedDateFormat } from '~common/helper/datetime';
import { ValidationRegex } from '~common/validator/validation-regex';
import { isNilOrEmpty } from '~common/helper/global';

export type ValidationMessage = () => string;

export type ValidationHandler<T> = (input: T | null | undefined) => boolean;

export interface Rule<T> {
  handler: ValidationHandler<T>;
  message: ValidationMessage;
}

export const isValid = (regexPattern: RegExp): ((value: string) => boolean) => (
  value: string,
): boolean => regexPattern.test(value);

export const isNotEmpty = (value: any): boolean => !isNil(value) && !isEmpty(value);

export const isNotBlank = (value: string): boolean => !isNilOrEmpty(value?.trim());

export const isGreaterThanZero = (value: number): boolean => value > 0;

export const isNotNegative = (value: number): boolean => value >= 0;

export const isNilOrGreaterThanZero = (value: number): boolean =>
  isGreaterThanZero(value) || isNil(value);

export const isValidStartEndDateTime = (startDateTime: string, endDateTime: string): boolean => {
  // if one of the dates missing return true thus allowing to use for non-required date fields
  // instead add required() to the list of rules
  if (isNil(startDateTime) || isNil(endDateTime)) {
    return true;
  }
  const parsedStart = datetime.parse(startDateTime);
  const parsedEnd = datetime.parse(endDateTime);
  return parsedStart?.isBefore(parsedEnd);
};

export const isValidDateAfter = (endDateTime: string) => (startDateTime: string): boolean =>
  isValidStartEndDateTime(startDateTime, endDateTime);

export const isValidDateBefore = (startDateTime: string) => (endDateTime: string): boolean =>
  isValidStartEndDateTime(startDateTime, endDateTime);

export const isFutureDate = (date: string): boolean => {
  const parsedDate = datetime.parse(date);
  return datetime.isUpcoming(parsedDate);
};

export const isValidBirthday = (value: string): boolean => {
  if (!isNotEmpty) {
    return false;
  }
  const date = datetime.parse(value, localeBasedDateFormat(), true);
  return !isNil(date);
};

export const isValidUrlPartial = (value: string): boolean => {
  if (!isNotEmpty) {
    return false;
  }
  return isValid(ValidationRegex.ALPHANUMERIC_DASH)(value);
};

export const isMinAgeRespected = (value: string): boolean => {
  const MIN_AGE = 13;
  const date = datetime.parse(value, localeBasedDateFormat(), true);
  if (isNil(date)) {
    return true; // isNotEmpty validator will return the error in that case
  }
  const age = datetime.diff(datetime.now(), date, 'year', true);
  return age >= MIN_AGE;
};

export const isMaxAgeRespected = (value: string): boolean => {
  const MAX_AGE = 120;
  const date = datetime.parse(value, localeBasedDateFormat(), true);
  if (!date) {
    return true; // isNotEmpty validator will return the error in that case
  }
  const age = datetime.diff(datetime.now(), date, 'year', true);
  return age <= MAX_AGE;
};

/**
 * Checks if a string contains at least 1 digit and 1 letter
 */
export const containsAtLeastOneDigitAndLetter = (value: string): boolean => {
  if (isNil(value) || isEmpty(value)) {
    return false;
  }
  return isValid(ValidationRegex.ONE_DIGIT_AND_LETTER)(value);
};

/**
 * Validate that a strings has a minimum length
 * @param minLength
 */
export const isMinLength = (minLength: number) => (value: string): boolean => {
  if (isNil(minLength) || isNil(value) || isEmpty(value)) {
    return false;
  }
  return value.length >= minLength;
};

export const isValidEmail = isValid(ValidationRegex.EMAIL_PATTERN);

export const isNotEqual = (otherValue: string) => (value: string): boolean => {
  return otherValue !== value;
};

export const isValidCanadianPostalCode = isValid(ValidationRegex.CANADIAN_POSTAL_CODE_REGEX);

export const isValidUSPostalCode = isValid(ValidationRegex.US_POSTAL_CODE_REGEX);

export const isValidCreditCardExpirationDate = (value: string): boolean => {
  if (isNil(value)) {
    return false;
  }
  const creditCardExpirationDate: CreditCardExpirationDate = creditCard.parseCardExpiry(value);
  if (isNil(creditCardExpirationDate)) {
    return false;
  }
  return creditCard.isValidExpirationDate(
    creditCardExpirationDate.month,
    creditCardExpirationDate.year,
  );
};

export const isValidCreditCardNumber = (value: string): boolean => creditCard.isValidNumber(value);

export const isValidCreditCardCVC = (creditCardNumber: string) => (cvc: string): boolean =>
  creditCard.isValidCVC(cvc, creditCardNumber);

export const isValidCVCByCardType = (cardType: CardType) => (cvc: string): boolean =>
  creditCard.isValidCVCByCardType(cvc, cardType);

/**
 * Hack validator that always returns true.
 * Use in cases where you need to bypass validation.
 * e.g. use as the validator for postal codes for countries for which we don't validate the postal code.
 * @param _value
 */
export const alwaysValid = (_value: any): boolean => true;



// WEBPACK FOOTER //
// /codebuild/output/src891534606/src/modules/common/src/validator/validation-rules.ts